import React, { useState, useEffect } from "react";
import { FiPlay, FiMaximize2, FiX } from "react-icons/fi";

const LastContentCampaign = ({ campaign }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState({});

  const videoBanner = campaign?.videoURL || null;
  const isYouTubeLink = (url) => /youtu\.?be/.test(url);

  const getYouTubeEmbedURL = (url) => {
    const videoIdMatch = url.match(
      /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/
    );
    return videoIdMatch
      ? `https://www.youtube.com/embed/${videoIdMatch[1]}?rel=0&modestbranding=1`
      : null;
  };

  // Collect all remaining images (after the first 2 of each shortInfo section)
  const remainingImages =
    campaign?.shortInfo?.flatMap(
      (section) => section.infoImages?.slice(2) || []
    ) || [];

  const handleImageClick = (img) => {
    setSelectedImage(img);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const handleImageLoad = (index) => {
    setImageLoading((prev) => ({ ...prev, [index]: false }));
  };

  const handleImageLoadStart = (index) => {
    setImageLoading((prev) => ({ ...prev, [index]: true }));
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    if (selectedImage) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [selectedImage]);

  return (
    <div className="mt-6 sm:mt-8 w-full">
      {/* Top Video Section */}
      {videoBanner && (
        <div className="mb-6 sm:mb-8">
          <div className="relative overflow-hidden rounded-lg shadow-lg bg-gray-100">
            {isYouTubeLink(videoBanner) ? (
              <div className="relative">
                <iframe
                  src={getYouTubeEmbedURL(videoBanner)}
                  title="Campaign video"
                  allowFullScreen
                  className="w-full aspect-video rounded-lg"
                  loading="lazy"
                />
                {/* Play button overlay */}
                <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full flex items-center text-sm">
                  <FiPlay className="w-4 h-4 mr-2" />
                  Watch Video
                </div>
              </div>
            ) : (
              <video
                src={videoBanner}
                controls
                className="w-full aspect-video object-cover rounded-lg"
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      )}

      {/* Remaining Images */}
      {remainingImages.length > 0 && (
        <div className="w-full">
          <div className="mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
              More Images
            </h3>
            <div className="w-16 h-1 bg-amber-500 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 w-full">
            {remainingImages.map((img, index) => (
              <div
                key={index}
                className="w-full group cursor-pointer"
                onClick={() => handleImageClick(img)}
              >
                <div className="relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 bg-gray-100">
                  {/* Loading spinner */}
                  {imageLoading[index] && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent"></div>
                    </div>
                  )}

                  <img
                    src={img}
                    alt="Campaign preview"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                    onLoadStart={() => handleImageLoadStart(index)}
                    onLoad={() => handleImageLoad(index)}
                  />

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-75 group-hover:scale-100">
                      <div className="bg-white/90 backdrop-blur-sm text-gray-700 p-3 rounded-full shadow-lg">
                        <FiMaximize2 className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full-Screen Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-10 text-white text-2xl hover:text-gray-300 transition-colors duration-200 bg-black/30 backdrop-blur-sm rounded-full p-3 hover:bg-black/50"
            onClick={closeModal}
            aria-label="Close image preview"
          >
            <FiX />
          </button>

          {/* ESC hint */}
          <div className="absolute top-4 left-4 text-white/70 text-sm bg-black/30 backdrop-blur-sm px-3 py-2 rounded-lg">
            Press ESC to close
          </div>

          {/* Modal content */}
          <div
            className="max-w-6xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Campaign gallery - Full size view"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LastContentCampaign;

//  {
//    remainingImages.length > 0 && (
//      <div
//        className="w-full"
//        // data-aos="fade-up" data-aos-delay="200"
//      >
//        <div className="mb-4 sm:mb-6">
//          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
//            More Images
//          </h3>
//          <div className="w-16 h-1 bg-amber-500 rounded-full"></div>
//        </div>

//        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 w-full">
//          {remainingImages.map((img, index) => (
//            <div
//              key={index}
//              className="w-full group cursor-pointer"
//              // data-aos="zoom-in"
//              // data-aos-delay={index * 50}
//            >
//              <div className="relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 bg-gray-100">
//                {/* Loading spinner */}
//                {imageLoading[index] && (
//                  <div className="absolute inset-0 flex items-center justify-center">
//                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent"></div>
//                  </div>
//                )}

//                <img
//                  src={img}
//                  alt=""
//                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
//                  loading="lazy"
//                  onClick={() => handleImageClick(img)}
//                  onLoadStart={() => handleImageLoadStart(index)}
//                  onLoad={() => handleImageLoad(index)}
//                />

//                {/* Overlay on hover */}
//                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
//                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-75 group-hover:scale-100">
//                    <div className="bg-white/90 backdrop-blur-sm text-gray-700 p-3 rounded-full shadow-lg">
//                      <FiMaximize2 className="w-5 h-5" />
//                    </div>
//                  </div>
//                </div>
//              </div>
//            </div>
//          ))}
//        </div>
//      </div>
//    );
//  }

//  {
//    selectedImage && (
//      <div
//        className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
//        onClick={closeModal}
//        role="dialog"
//        aria-modal="true"
//        aria-labelledby="image-modal-title"
//      >
//        {/* Close button */}
//        <button
//          className="absolute top-4 right-4 z-10 text-white text-2xl hover:text-gray-300 transition-colors duration-200 bg-black/30 backdrop-blur-sm rounded-full p-3 hover:bg-black/50"
//          onClick={closeModal}
//          aria-label="Close image preview"
//        >
//          <FiX />
//        </button>

//        {/* ESC hint */}
//        <div className="absolute top-4 left-4 text-white/70 text-sm bg-black/30 backdrop-blur-sm px-3 py-2 rounded-lg">
//          Press ESC to close
//        </div>

//        {/* Modal content */}
//        <div
//          className="max-w-6xl w-full flex flex-col items-center"
//          onClick={(e) => e.stopPropagation()}
//        >
//          <img
//            src={selectedImage}
//            alt="Campaign gallery - Full size view"
//            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
//            style={{
//              maxHeight: "calc(100vh - 80px)",
//              maxWidth: "calc(100vw - 32px)",
//            }}
//          />
//        </div>
//      </div>
//    );
//  }
